import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../providers/AuthProvider';
import { adminCycleService, ciKeyForCode, CycleInput } from '../../services/adminCycleService';
import { userService } from '../../services/userService';
import { Cycle, fetchCourseConfigSuccess, selectCourseConfig, UserStory } from '../../store/appDataStore';
import { selectUserInfos } from '../../store/userDataStore';

const SESSION_TYPES = ['CM', 'TD', 'TP', 'Projet', 'CCS', 'Autre'];

type Bound = 'start' | 'end';

interface CycleForm {
  name: string;
  short_name: string;
  branches: string;            // séparées par des virgules
  binome_only_branches: string[];
  solo_fallback: string;       // « A=B », une règle par ligne
  dates_start: string;         // type de séance ('' = pas de date calculée)
  dates_end: string;
}

const EMPTY_FORM: CycleForm = {
  name: '', short_name: '', branches: '', binome_only_branches: [], solo_fallback: '', dates_start: '', dates_end: '',
};

const SourceChip: React.FC<{ source: Cycle['start_source'] }> = ({ source }) => {
  if (source === 'manual') return <Chip size="small" color="primary" label="saisie" />;
  if (source === 'sessions') return <Chip size="small" variant="outlined" label="calculée depuis les séances" />;
  return <Chip size="small" variant="outlined" color="warning" label="non définie" />;
};

const frDate = (iso?: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

function formFromCycle(cycle: Cycle): CycleForm {
  return {
    name: cycle.name,
    short_name: cycle.short_name,
    branches: (cycle.branches || []).join(', '),
    binome_only_branches: cycle.binome_only_branches || [],
    solo_fallback: Object.entries(cycle.solo_fallback || {}).map(([a, b]) => `${a}=${b}`).join('\n'),
    dates_start: cycle.default_dates_from_sessions?.start || '',
    dates_end: cycle.default_dates_from_sessions?.end || '',
  };
}

function inputFromForm(form: CycleForm): CycleInput {
  const fallback: Record<string, string> = {};
  form.solo_fallback.split('\n').map(l => l.trim()).filter(Boolean).forEach(line => {
    const [from, to] = line.split('=').map(part => part.trim());
    if (from && to) fallback[from] = to;
  });
  return {
    name: form.name.trim(),
    short_name: form.short_name.trim(),
    branches: form.branches.split(',').map(b => b.trim()).filter(Boolean),
    binome_only_branches: form.binome_only_branches,
    solo_fallback: fallback,
    default_dates_from_sessions: form.dates_start && form.dates_end ? { start: form.dates_start, end: form.dates_end } : null,
  };
}

// Admin Cycles : cycles calés sur des branches, leurs US/TS (codes, points, branches) et leurs dates.
// Découper un cycle (ex. TP-Projet en sous-cycles) : bouton ✂ sur la story qui commence le nouveau cycle.
// Un résultat CI de la branche compte pour chaque sous-cycle, chacun sur ses propres stories.
const AdminCyclesPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const dispatch = useDispatch();
  const courseConfig = useSelector(selectCourseConfig);
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';
  const cycles: Cycle[] = courseConfig?.cycles || [];

  const [drafts, setDrafts] = useState<Record<number, { start: string; end: string }>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Cycle | 'new' | null>(null);
  const [form, setForm] = useState<CycleForm>(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState<Cycle | null>(null);
  const [splitting, setSplitting] = useState<{ cycle: Cycle; story: UserStory; name: string; short_name: string } | null>(null);
  const [newStory, setNewStory] = useState<Record<number, { code: string; description: string; points: string }>>({});
  const [pointDrafts, setPointDrafts] = useState<Record<number, string>>({});

  // Brouillon = dates saisies uniquement : une date calculée reste vide dans le champ, pour ne pas
  // la figer en « saisie » à l'enregistrement
  useEffect(() => {
    setDrafts(Object.fromEntries(cycles.map((c: Cycle) => [c.id, {
      start: c.start_source === 'manual' ? c.start_date || '' : '',
      end: c.end_source === 'manual' ? c.end_date || '' : '',
    }])));
    setPointDrafts({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseConfig]);

  // Lance une action d'admin puis recharge la configuration du cours (cycles + dates) pour tout le front
  const run = async (action: (token: string) => Promise<{ message?: string }>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const result = await action(token);
      setMessage(result?.message || 'Enregistré');
      dispatch(fetchCourseConfigSuccess(await userService.getCourseConfig(token)));
      return true;
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'enregistrement");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }
  if (!courseConfig) return null;

  const moveCycle = (index: number, delta: number) => {
    const ids = cycles.map(c => c.id);
    [ids[index], ids[index + delta]] = [ids[index + delta], ids[index]];
    run(token => adminCycleService.reorderCycles(ids, token));
  };

  const moveStory = (cycle: Cycle, index: number, delta: number) => {
    const ids = cycle.user_stories.map(us => us.id);
    [ids[index], ids[index + delta]] = [ids[index + delta], ids[index]];
    run(token => adminCycleService.reorderStories(cycle.id, ids, token));
  };

  const saveCycle = async () => {
    const data = inputFromForm(form);
    const ok = await run(token => (editing === 'new'
      ? adminCycleService.createCycle(data, token)
      : adminCycleService.updateCycle((editing as Cycle).id, data, token)));
    if (ok) setEditing(null);
  };

  const addStory = (cycle: Cycle) => {
    const draft = newStory[cycle.id] || { code: '', description: '', points: '1' };
    run(token => adminCycleService.addStory(cycle.id, {
      code: draft.code.trim(), description: draft.description.trim(), story_points: parseInt(draft.points, 10) || 0,
    }, token)).then(ok => ok && setNewStory({ ...newStory, [cycle.id]: { code: '', description: '', points: '1' } }));
  };

  const dateField = (cycle: Cycle, bound: Bound) => {
    const computed = bound === 'start' ? cycle.computed_start_date : cycle.computed_end_date;
    return (
      <TextField
        type="date"
        size="small"
        label={bound === 'start' ? 'Début' : 'Fin'}
        value={drafts[cycle.id]?.[bound] || ''}
        slotProps={{ inputLabel: { shrink: true } }}
        helperText={computed ? `Calculée : ${frDate(computed)} — vider le champ pour l'utiliser` : ' '}
        onChange={(e) => setDrafts({ ...drafts, [cycle.id]: { ...drafts[cycle.id], [bound]: e.target.value } })}
      />
    );
  };

  const formBranches = form.branches.split(',').map(b => b.trim()).filter(Boolean);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h5">Admin Cycles</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(EMPTY_FORM); setEditing('new'); }}>
          Nouveau cycle
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Un cycle est calé sur une ou plusieurs branches. Pour découper un cycle (ex. TP-Projet en sous-cycles),
        cliquez « Découper » sur ce cycle (ou ✂ sur une story) et choisissez la story qui commence le nouveau cycle :
        elle et les suivantes y sont déplacées. Un résultat CI
        de la branche compte alors pour chaque sous-cycle, chacun sur ses propres stories. Les scores sont recalculés
        à chaque modification (points, découpage).
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      {cycles.map((cycle, cycleIndex) => {
        const totalPoints = cycle.user_stories.reduce((acc, us) => acc + (us.story_points || 0), 0);
        const draft = newStory[cycle.id] || { code: '', description: '', points: '1' };
        const sharesBranch = cycles.filter(c => c.id !== cycle.id && c.branches.some(b => cycle.branches.includes(b)));
        return (
          <Paper key={cycle.id} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6">{cycle.name}</Typography>
              <Typography variant="body2" color="text.secondary">({cycle.short_name})</Typography>
              {cycle.branches.map(b => <Chip key={b} size="small" label={b} />)}
              {sharesBranch.length > 0 && (
                <Chip size="small" variant="outlined" color="info"
                  label={`même branche que : ${sharesBranch.map(c => c.name).join(', ')}`} />
              )}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Monter"><span><IconButton size="small" disabled={busy || cycleIndex === 0} onClick={() => moveCycle(cycleIndex, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
              <Tooltip title="Descendre"><span><IconButton size="small" disabled={busy || cycleIndex === cycles.length - 1} onClick={() => moveCycle(cycleIndex, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
              <Tooltip title={cycle.user_stories.length < 2
                ? 'Ajoutez au moins deux US/TS pour pouvoir découper ce cycle'
                : 'Découper ce cycle : choisir la story qui commence le nouveau cycle'}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<ContentCutIcon />} disabled={busy || cycle.user_stories.length < 2}
                    onClick={() => setSplitting({ cycle, story: cycle.user_stories[Math.ceil(cycle.user_stories.length / 2)],
                                                  name: `${cycle.name} 2`, short_name: `${cycle.short_name}-2` })}>
                    Découper
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Modifier le cycle"><IconButton size="small" onClick={() => { setForm(formFromCycle(cycle)); setEditing(cycle); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Supprimer le cycle"><IconButton size="small" color="error" onClick={() => setConfirmDelete(cycle)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ minWidth: 220 }}>
                <Typography variant="body2">Dates effectives : {frDate(cycle.start_date)} → {frDate(cycle.end_date)}</Typography>
                <SourceChip source={cycle.start_source} /> <SourceChip source={cycle.end_source} />
              </Box>
              {dateField(cycle, 'start')}
              {dateField(cycle, 'end')}
              <Button variant="outlined" size="small" disabled={busy} sx={{ mt: 0.5 }}
                onClick={() => run(token => adminCycleService.setDates(cycle.id, {
                  start_date: drafts[cycle.id]?.start || null, end_date: drafts[cycle.id]?.end || null,
                }, token))}>
                Enregistrer les dates
              </Button>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Clé CI</TableCell>
                  <TableCell>Description</TableCell>
                  {cycle.branches.length > 1 && <TableCell>Branches</TableCell>}
                  <TableCell align="right">Points</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {cycle.user_stories.map((us, index) => (
                  <TableRow key={us.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{us.code}</TableCell>
                    <TableCell><code>{ciKeyForCode(us.code)}</code></TableCell>
                    <TableCell>{us.description}</TableCell>
                    {cycle.branches.length > 1 && <TableCell>{(us.branches || []).join(', ') || 'toutes'}</TableCell>}
                    <TableCell align="right" sx={{ width: 110 }}>
                      <TextField
                        type="number" size="small" sx={{ width: 80 }}
                        value={pointDrafts[us.id] ?? String(us.story_points)}
                        onChange={(e) => setPointDrafts({ ...pointDrafts, [us.id]: e.target.value })}
                        onBlur={() => {
                          const value = parseInt(pointDrafts[us.id] ?? '', 10);
                          if (!Number.isNaN(value) && value !== us.story_points) {
                            run(token => adminCycleService.updateStory(us.id, { story_points: value }, token));
                          }
                        }}
                        slotProps={{ htmlInput: { min: 0, max: 100 } }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Monter"><span><IconButton size="small" disabled={busy || index === 0} onClick={() => moveStory(cycle, index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <Tooltip title="Descendre"><span><IconButton size="small" disabled={busy || index === cycle.user_stories.length - 1} onClick={() => moveStory(cycle, index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                      <TextField select size="small" value="" sx={{ width: 150, mx: 1 }} label="Déplacer vers"
                        onChange={(e) => run(token => adminCycleService.updateStory(us.id, { cycle_id: Number(e.target.value) }, token))}>
                        {cycles.filter(c => c.id !== cycle.id).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                      </TextField>
                      <Tooltip title={index === 0 ? 'Choisir une autre story que la première' : `Nouveau cycle à partir de ${us.code}`}>
                        <span>
                          <IconButton size="small" disabled={busy || index === 0}
                            onClick={() => setSplitting({ cycle, story: us, name: `${cycle.name} 2`, short_name: `${cycle.short_name}-2` })}>
                            <ContentCutIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Supprimer"><IconButton size="small" color="error" disabled={busy}
                        onClick={() => window.confirm(`Supprimer ${us.code} ?`) && run(token => adminCycleService.deleteStory(us.id, token))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <TextField size="small" placeholder="TP.US1" value={draft.code} sx={{ width: 140 }}
                      onChange={(e) => setNewStory({ ...newStory, [cycle.id]: { ...draft, code: e.target.value } })} />
                  </TableCell>
                  <TableCell><code>{draft.code ? ciKeyForCode(draft.code) : ''}</code></TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth placeholder="Description" value={draft.description}
                      onChange={(e) => setNewStory({ ...newStory, [cycle.id]: { ...draft, description: e.target.value } })} />
                  </TableCell>
                  {cycle.branches.length > 1 && <TableCell>toutes</TableCell>}
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 80 }} value={draft.points}
                      onChange={(e) => setNewStory({ ...newStory, [cycle.id]: { ...draft, points: e.target.value } })} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<AddIcon />} disabled={busy || !draft.code.trim()} onClick={() => addStory(cycle)}>
                      Ajouter
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {cycle.user_stories.length} story(s) · {totalPoints} point(s)
            </Typography>
          </Paper>
        );
      })}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing === 'new' ? 'Nouveau cycle' : 'Modifier le cycle'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Nom" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Nom court" size="small" value={form.short_name} helperText="Minuscules, chiffres et tirets (ex. tp-projet-2)"
            onChange={(e) => setForm({ ...form, short_name: e.target.value })} />
          <TextField label="Branches" size="small" value={form.branches}
            helperText="Branches étudiantes du cycle, séparées par des virgules (ex. tp-projet). Plusieurs cycles peuvent partager une branche."
            onChange={(e) => setForm({ ...form, branches: e.target.value })} />
          {formBranches.length > 1 && (
            <>
              <TextField select label="Branches réservées aux binômes" size="small"
                value={form.binome_only_branches.filter(b => formBranches.includes(b))}
                slotProps={{ select: { multiple: true } }}
                onChange={(e) => setForm({ ...form, binome_only_branches: e.target.value as unknown as string[] })}>
                {formBranches.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              <TextField label="Remplacement pour un monôme" size="small" multiline minRows={1} value={form.solo_fallback}
                helperText="Une règle par ligne : branche attendue=branche acceptée à la place (ex. prepa-student-A=prepa-student-B)"
                onChange={(e) => setForm({ ...form, solo_fallback: e.target.value })} />
            </>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="Début calculé : 1re séance de type" size="small" sx={{ flex: 1 }} value={form.dates_start}
              onChange={(e) => setForm({ ...form, dates_start: e.target.value })}>
              <MenuItem value="">— (pas de date calculée)</MenuItem>
              {SESSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select label="Fin calculée : dernière séance de type" size="small" sx={{ flex: 1 }} value={form.dates_end}
              onChange={(e) => setForm({ ...form, dates_end: e.target.value })}>
              <MenuItem value="">—</MenuItem>
              {SESSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Annuler</Button>
          <Button variant="contained" disabled={busy || !form.name.trim() || !form.short_name.trim() || formBranches.length === 0}
            onClick={saveCycle}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={splitting !== null} onClose={() => setSplitting(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Découper « {splitting?.cycle.name} »</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography variant="body2">
            Nouveau cycle sur les mêmes branches ({splitting?.cycle.branches.join(', ')}), placé juste après, avec la story
            choisie et toutes celles qui la suivent. Un résultat CI de la branche comptera pour les deux cycles, chacun sur
            ses propres stories.
          </Typography>
          <TextField select label="Le nouveau cycle commence à" size="small" value={splitting?.story.id ?? ''}
            onChange={(e) => {
              const story = splitting?.cycle.user_stories.find(us => us.id === Number(e.target.value));
              if (splitting && story) setSplitting({ ...splitting, story });
            }}>
            {(splitting?.cycle.user_stories || []).slice(1).map(us => (
              <MenuItem key={us.id} value={us.id}>{us.code}{us.description ? ` — ${us.description}` : ''}</MenuItem>
            ))}
          </TextField>
          {splitting && (() => {
            const index = splitting.cycle.user_stories.findIndex(us => us.id === splitting.story.id);
            const kept = splitting.cycle.user_stories.slice(0, index).map(us => us.code).join(', ');
            const moved = splitting.cycle.user_stories.slice(index).map(us => us.code).join(', ');
            return (
              <Typography variant="body2" color="text.secondary">
                « {splitting.cycle.name} » garde : {kept}<br />« {splitting.name || 'nouveau cycle'} » reçoit : {moved}
              </Typography>
            );
          })()}
          <TextField label="Nom du nouveau cycle" size="small" value={splitting?.name || ''}
            onChange={(e) => splitting && setSplitting({ ...splitting, name: e.target.value })} />
          <TextField label="Nom court" size="small" value={splitting?.short_name || ''}
            onChange={(e) => splitting && setSplitting({ ...splitting, short_name: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitting(null)}>Annuler</Button>
          <Button variant="contained" disabled={busy || !splitting?.name.trim() || !splitting?.short_name.trim()}
            onClick={async () => {
              if (!splitting) return;
              const ok = await run(token => adminCycleService.splitCycle(splitting.cycle.id, {
                from_story_id: splitting.story.id, name: splitting.name.trim(), short_name: splitting.short_name.trim(),
              }, token));
              if (ok) setSplitting(null);
            }}>
            Découper
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Supprimer « {confirmDelete?.name} » ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Le cycle et ses {confirmDelete?.user_stories.length ?? 0} story(s) sont supprimés. Les résultats CI restent en base
            et continuent de compter pour les autres cycles de leur branche ; sans autre cycle sur la branche, ils ne comptent
            plus dans les scores.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" disabled={busy}
            onClick={async () => {
              if (!confirmDelete) return;
              const ok = await run(token => adminCycleService.deleteCycle(confirmDelete.id, token));
              if (ok) setConfirmDelete(null);
            }}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCyclesPage;
